import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const meses = [
  { num: 1, label: 'Janeiro' }, { num: 2, label: 'Fevereiro' }, { num: 3, label: 'Março' },
  { num: 4, label: 'Abril' }, { num: 5, label: 'Maio' }, { num: 6, label: 'Junho' },
  { num: 7, label: 'Julho' }, { num: 8, label: 'Agosto' }, { num: 9, label: 'Setembro' },
  { num: 10, label: 'Outubro' }, { num: 11, label: 'Novembro' }, { num: 12, label: 'Dezembro' },
];

function useCountUp(target, duration = 1000) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setValue(target); clearInterval(timer); }
      else setValue(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return value;
}

function AnimatedCard({ label, valor, cor, subtext, icone, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const animated = useCountUp(visible ? valor : 0, 1000);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  const formatarMoeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="rounded-2xl p-6 border transition-all duration-500"
      style={{ backgroundColor: '#111', borderColor: '#1f1f1f', opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}>
      <div className="flex justify-between items-start mb-4">
        <p className="text-sm font-medium" style={{ color: '#666' }}>{label}</p>
        <span className="text-xl">{icone}</span>
      </div>
      <p className="text-3xl font-bold mb-1" style={{ color: cor }}>{formatarMoeda(animated)}</p>
      {subtext && <p className="text-xs mt-1" style={{ color: '#444' }}>{subtext}</p>}
    </div>
  );
}

function BarraProgresso({ label, valor, total, cor }) {
  const [width, setWidth] = useState(0);
  const pct = total > 0 ? (valor / total) * 100 : 0;
  useEffect(() => { const t = setTimeout(() => setWidth(pct), 300); return () => clearTimeout(t); }, [pct]);
  const formatarMoeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-white">{label}</span>
        <span className="text-sm font-semibold" style={{ color: cor }}>{formatarMoeda(valor)}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#1f1f1f' }}>
        <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${width}%`, backgroundColor: cor }} />
      </div>
      <p className="text-xs mt-1" style={{ color: '#444' }}>{pct.toFixed(1)}% do total</p>
    </div>
  );
}

function Dashboard() {
  const hoje = new Date();
  const diaHoje = hoje.getDate();
  const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(hoje.getFullYear());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    buscarDados();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSelecionado, anoSelecionado]);

  const buscarDados = async () => {
    setCarregando(true);
    const { data: clientes } = await supabase.from('clientes').select('*').eq('ativo', true);
    const { data: pagamentos } = await supabase.from('pagamentos').select('*').eq('mes', String(mesSelecionado)).eq('ano', String(anoSelecionado));
    const { data: despesas } = await supabase.from('despesas').select('*').eq('mes', String(mesSelecionado)).eq('ano', String(anoSelecionado));

    const receitaTotal = (clientes || []).reduce((acc, c) => acc + Number(c.valor), 0);
    const receitaRecebida = (clientes || []).filter(c => (pagamentos || []).find(p => p.cliente_id === c.id && p.pago)).reduce((acc, c) => acc + Number(c.valor), 0);
    const totalDespesas = (despesas || []).reduce((acc, d) => acc + Number(d.valor), 0);
    const lucro = receitaRecebida - totalDespesas;
    const porCategoria = {};
    (despesas || []).forEach(d => { porCategoria[d.categoria] = (porCategoria[d.categoria] || 0) + Number(d.valor); });
    const clientesPagos = (clientes || []).filter(c => (pagamentos || []).find(p => p.cliente_id === c.id && p.pago));
    const clientesPendentes = (clientes || []).filter(c => !(pagamentos || []).find(p => p.cliente_id === c.id && p.pago));

    const pagarHoje = (despesas || []).filter(d => {
      if (d.pago) return false;
      if (d.categoria === 'fixa') return diaHoje === 1;
      if (d.categoria === 'cartao' || d.categoria === 'parcela') return diaHoje === 15;
      return false;
    });

    setDados({ receitaTotal, receitaRecebida, receitaPendente: receitaTotal - receitaRecebida, totalDespesas, lucro, porCategoria, clientesPagos, clientesPendentes, pagarHoje, clientes: clientes || [] });
    setCarregando(false);
  };

  const marcarDespesaPaga = async (id) => {
    await supabase.from('despesas').update({ pago: true }).eq('id', id);
    buscarDados();
  };

  const formatarMoeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  if (carregando) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#CCFF00', borderTopColor: 'transparent' }}></div>
        <p style={{ color: '#444' }}>Carregando...</p>
      </div>
    </div>
  );

  const { receitaTotal, receitaRecebida, receitaPendente, totalDespesas, lucro, porCategoria, clientesPagos, clientesPendentes, pagarHoje } = dados;
  const pctRecebido = receitaTotal > 0 ? (receitaRecebida / receitaTotal) * 100 : 0;
  const circunferencia = 2 * Math.PI * 54;

  return (
    <div>
      {/* Cabeçalho */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-white">Dashboard</h2>
          <p className="mt-1 text-sm" style={{ color: '#555' }}>{meses.find(m => m.num === mesSelecionado)?.label} de {anoSelecionado}</p>
        </div>
        <div className="flex gap-2">
          <select value={mesSelecionado} onChange={(e) => setMesSelecionado(Number(e.target.value))}
            className="text-white rounded-xl px-4 py-2 text-sm outline-none"
            style={{ backgroundColor: '#111', border: '1px solid #222' }}>
            {meses.map(m => <option key={m.num} value={m.num}>{m.label}</option>)}
          </select>
          <select value={anoSelecionado} onChange={(e) => setAnoSelecionado(Number(e.target.value))}
            className="text-white rounded-xl px-4 py-2 text-sm outline-none"
            style={{ backgroundColor: '#111', border: '1px solid #222' }}>
            {[2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Pagar Hoje */}
      {pagarHoje.length > 0 && (
        <div className="mb-8 rounded-2xl p-6" style={{ backgroundColor: '#111', border: '1px solid #2a1f00' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ backgroundColor: '#CCFF0020' }}>🔔</div>
            <h3 className="text-white font-semibold">Pagar Hoje</h3>
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#CCFF0020', color: '#CCFF00' }}>{pagarHoje.length} pendentes</span>
          </div>
          <div className="space-y-2">
            {pagarHoje.map((d) => (
              <div key={d.id} className="flex justify-between items-center py-3 rounded-xl px-4" style={{ backgroundColor: '#0a0a0a' }}>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${d.categoria === 'fixa' ? 'text-blue-400' : d.categoria === 'cartao' ? 'text-purple-400' : 'text-orange-400'}`}
                    style={{ backgroundColor: d.categoria === 'fixa' ? '#1e3a5f' : d.categoria === 'cartao' ? '#2d1b69' : '#3d2000' }}>
                    {d.categoria === 'fixa' ? 'Fixa' : d.categoria === 'cartao' ? 'Cartão' : 'Parcela'}
                  </span>
                  <span className="text-white text-sm">{d.nome}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-sm" style={{ color: '#ff4444' }}>{formatarMoeda(d.valor)}</span>
                  <button onClick={() => marcarDespesaPaga(d.id)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
                    style={{ backgroundColor: '#CCFF0020', color: '#CCFF00' }}>
                    ✓ Pago
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cards principais */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <AnimatedCard label="Receita do Mês" valor={receitaTotal} cor="#ffffff" icone="💼" subtext={`${clientesPagos.length + clientesPendentes.length} clientes ativos`} delay={0} />
        <AnimatedCard label="Recebido" valor={receitaRecebida} cor="#CCFF00" icone="✅" subtext={`${clientesPagos.length} clientes pagaram`} delay={100} />
        <AnimatedCard label="A Receber" valor={receitaPendente} cor="#f59e0b" icone="⏳" subtext={`${clientesPendentes.length} pendentes`} delay={200} />
        <AnimatedCard label="Despesas" valor={totalDespesas} cor="#ff4444" icone="📊" subtext={lucro >= 0 ? `Lucro: ${formatarMoeda(lucro)}` : `Prejuízo: ${formatarMoeda(Math.abs(lucro))}`} delay={300} />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Despesas por categoria */}
        <div className="col-span-2 rounded-2xl p-6" style={{ backgroundColor: '#111', border: '1px solid #1f1f1f' }}>
          <h3 className="text-white font-semibold mb-6">Despesas por Categoria</h3>
          {Object.keys(porCategoria).length === 0 ? (
            <p className="text-sm" style={{ color: '#444' }}>Nenhuma despesa neste período.</p>
          ) : (
            Object.entries(porCategoria).map(([cat, val]) => {
              const total = Object.values(porCategoria).reduce((a, b) => a + b, 0);
              const cores = { fixa: '#3b82f6', cartao: '#a855f7', parcela: '#f97316' };
              const labels = { fixa: 'Fixas', cartao: 'Cartão', parcela: 'Parcelas' };
              return <BarraProgresso key={cat} label={labels[cat] || cat} valor={val} total={total} cor={cores[cat] || '#888'} />;
            })
          )}
        </div>

        {/* Recebimentos */}
        <div className="rounded-2xl p-6 flex flex-col" style={{ backgroundColor: '#111', border: '1px solid #1f1f1f' }}>
          <h3 className="text-white font-semibold mb-6">Recebimentos</h3>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative w-36 h-36 mb-6">
              <svg viewBox="0 0 120 120" className="w-36 h-36 -rotate-90">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#1f1f1f" strokeWidth="8" />
                <circle cx="60" cy="60" r="54" fill="none" stroke="#CCFF00" strokeWidth="8"
                  strokeDasharray={circunferencia}
                  strokeDashoffset={circunferencia - (pctRecebido / 100) * circunferencia}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 1.2s ease-out' }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-3xl font-bold text-white">{clientesPagos.length}</p>
                <p className="text-xs" style={{ color: '#555' }}>de {clientesPagos.length + clientesPendentes.length}</p>
              </div>
            </div>
            <div className="w-full space-y-3">
              <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ backgroundColor: '#0a0a0a' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#CCFF00' }}></div>
                  <span className="text-sm text-white">Pagos</span>
                </div>
                <span className="font-semibold text-sm" style={{ color: '#CCFF00' }}>{clientesPagos.length}</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ backgroundColor: '#0a0a0a' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
                  <span className="text-sm text-white">Pendentes</span>
                </div>
                <span className="font-semibold text-sm" style={{ color: '#f59e0b' }}>{clientesPendentes.length}</span>
              </div>
              <div className="flex justify-between items-center py-2 px-3 rounded-lg" style={{ backgroundColor: '#0a0a0a' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#CCFF00' }}></div>
                  <span className="text-sm text-white">Taxa</span>
                </div>
                <span className="font-semibold text-sm" style={{ color: '#CCFF00' }}>{pctRecebido.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;